import { API_BASE } from "./config";
import { bounceIfUnauthorized } from "./session";

/**
 * Auto-translate — on-demand, per sentence (日本-102).
 *
 * **Translation is never automatic.** A sentence the reader understood stays bare: there is no
 * translate-on-capture, no batch pass over a log, no whole-log parallel view. This module has one
 * verb for that reason, and it is only ever called from a button.
 *
 * **Producing a translation is here; editing and removing one are not.** Those are
 * `updateSentence({ translation })` in `reading.ts` — they are edits to a field on a sentence, and
 * the reading client already owns that field. Splitting them by who *wrote* the value rather than
 * by what is being written would give this module a second copy of every guard that has.
 */

export interface Translation {
  sentenceId: string;
  translation: string;
  /** Which model answered. Nothing renders it — the design cut the provenance line. */
  model: string;
}

/**
 * One row of the `Context…` panel.
 *
 * `items` and `tokens` are separately nullable and mean **different absences**: `items` is null for
 * a tier that cannot be counted from here (an unbuilt one), `tokens` for a panel opened without a
 * sentence to price — from the library, where `prior` is five of *which* sentences? Both render as
 * `—`, never as `0`, which would claim the tier is empty.
 */
export interface TranslateTier {
  key: string;
  label: string;
  enabled: boolean;
  /** False for the four tiers the design specifies and this pass does not build. */
  available: boolean;
  items: number | null;
  tokens: number | null;
  note: string;
}

export interface TranslateContext {
  folderId: string | null;
  folderName: string;
  /** How many logs this envelope speaks for — the panel's `Applies to all N logs` line. */
  logCount: number;
  /** False for a log with no folder: there is no work to configure, so the panel is read-only. */
  editable: boolean;
  priorEnabled: boolean;
  priorDepth: number;
  tiers: TranslateTier[];
  totalTokens: number | null;
  /**
   * Whether this deployment can translate at all — i.e. whether it has an API key.
   *
   * The same question the ghost `訳 translate` button asks, which is why it rides on the panel
   * payload rather than having a route: answering it from two places is how the button and the
   * panel end up disagreeing about whether the feature exists.
   */
  configured: boolean;
}

async function json<T>(path: string, init?: RequestInit, what = "translate"): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { cache: "no-store", ...init });
  if (!res.ok) {
    bounceIfUnauthorized(res);
    const detail = await res
      .json()
      .then((b) => (b as { detail?: string }).detail)
      .catch(() => undefined);
    throw new Error(detail ?? `Could not ${what} (${res.status})`);
  }
  return res.json();
}

/**
 * Translate one sentence and write the English onto it.
 *
 * **`retranslate` is this same call.** The reader presses it because they disagreed with the
 * answer, so there is nothing to memoise — a cache would make the button do nothing.
 */
export function translateSentence(sentenceId: string): Promise<Translation> {
  return json<Translation>(
    `/api/translate/sentences/${sentenceId}`,
    { method: "POST" },
    "translate the sentence",
  );
}

/**
 * The `Context…` panel, from whichever end the caller came in.
 *
 * Three depths of one question — a sentence knows its log, a log knows its folder — and only the
 * sentence can price the envelope. Called with nothing it answers the folderless default, which is
 * also the cheapest way to ask *is translation configured at all?*
 */
export function fetchContext(
  scope: { sentenceId?: string; logId?: string; folderId?: string } = {},
): Promise<TranslateContext> {
  const params = new URLSearchParams();
  if (scope.sentenceId) params.set("sentenceId", scope.sentenceId);
  if (scope.logId) params.set("logId", scope.logId);
  if (scope.folderId) params.set("folderId", scope.folderId);
  const query = params.toString();
  return json<TranslateContext>(
    `/api/translate/context${query ? `?${query}` : ""}`,
    undefined,
    "read the context settings",
  );
}

/** Save a work's envelope. Answers the whole panel, so the label and the price cannot drift. */
export function updateContext(
  folderId: string,
  body: { priorEnabled?: boolean; priorDepth?: number },
): Promise<TranslateContext> {
  return json<TranslateContext>(
    `/api/translate/context/${folderId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    "save the context settings",
  );
}
